import * as Slider from '@radix-ui/react-slider'
import { useSearchParams } from '@remix-run/react'
import { useCallback, useEffect, useState } from 'react'

type SliderRangeInputProps = {
  title: string
  minParamName: string
  maxParamName: string
  min: number
  max: number
  step?: number
  suffix?: string
  formatValue?: (value: number) => string
  validate?: (min: number, max: number) => boolean
}

export const SliderRangeInput = ({
  title,
  minParamName,
  maxParamName,
  min,
  max,
  step = 1,
  suffix = '',
  formatValue,
  validate,
}: SliderRangeInputProps) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [values, setValues] = useState([
    parseInt(searchParams.get(minParamName) || min.toString(), 10),
    parseInt(searchParams.get(maxParamName) || max.toString(), 10),
  ])

  useEffect(() => {
    const urlMin = searchParams.get(minParamName)
    const urlMax = searchParams.get(maxParamName)

    setValues([
      urlMin ? parseInt(urlMin, 10) : min,
      urlMax ? parseInt(urlMax, 10) : max,
    ])
  }, [searchParams, minParamName, maxParamName, min, max])

  const updateSearchParams = useCallback(
    (newMin: number, newMax: number) => {
      const currentParams = Object.fromEntries(searchParams.entries())
      const updatedParams = { ...currentParams }

      if (newMin === min) {
        delete updatedParams[minParamName]
      } else {
        updatedParams[minParamName] = newMin.toString()
      }

      if (newMax === max) {
        delete updatedParams[maxParamName]
      } else {
        updatedParams[maxParamName] = newMax.toString()
      }

      setSearchParams(updatedParams)
    },
    [searchParams, setSearchParams, minParamName, maxParamName, min, max],
  )

  const handleValueChange = (newValues: number[]) => {
    const newMin = newValues[0] ?? min
    const newMax = newValues[1] ?? max
    setValues(newValues)

    if (!validate || validate(newMin, newMax)) {
      updateSearchParams(newMin, newMax)
    }
  }

  const formatDisplayValue = (value: number) => {
    if (formatValue) {
      return formatValue(value)
    }
    return value + suffix
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <h3 className="text-base-bold">{title}</h3>

      <div className="relative px-2">
        <Slider.Root
          className={`
            relative flex h-5 w-full touch-none select-none items-center
          `}
          value={values}
          onValueChange={handleValueChange}
          max={max}
          min={min}
          step={step}
          minStepsBetweenThumbs={1}
        >
          <Slider.Track
            className={`relative h-[4px] grow rounded-full bg-neutral-200`}
          >
            <Slider.Range className="absolute h-full rounded-full bg-primary" />
          </Slider.Track>
          <Slider.Thumb
            className={`
              block h-5 w-5 cursor-pointer rounded-full border-2 border-primary
              bg-white shadow-lg

              focus:outline-none focus:ring-2 focus:ring-primary
              focus:ring-offset-2
            `}
          />
          <Slider.Thumb
            className={`
              block h-5 w-5 cursor-pointer rounded-full border-2 border-primary
              bg-white shadow-lg

              focus:outline-none focus:ring-2 focus:ring-primary
              focus:ring-offset-2
            `}
          />
        </Slider.Root>

        <div className="mt-4 flex justify-between text-sm text-neutral-600">
          <span>{formatDisplayValue(values[0] ?? min)}</span>
          <span>{formatDisplayValue(values[1] ?? max)}</span>
        </div>
      </div>
    </div>
  )
}
